import type { FC, ReactNode } from 'react'
import type { Product } from '@/types'

import Link from 'next/link'
import { ShoppingBag } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatIDR } from '@/lib/format'
import { useShop } from '@/components/providers/shop-provider'
import { ProductImage } from '@/components/shop/product-image'
import { WishlistButton } from '@/components/shop/shop-bits'

interface ProductCardProps {
    product: Product
    priority?: boolean
    className?: string
    tone?: 'light' | 'dark'
}

export const ProductCard: FC<ProductCardProps> = ({ product, priority = false, className, tone = 'light' }): ReactNode => {
    const { addToCart } = useShop()
    const color = product.colors[0] ?? null
    const soldOut = !product.madeToOrder && product.stock <= 0
    const lowStock = !product.madeToOrder && product.stock > 0 && product.stock <= 3
    const dark = tone === 'dark'

    const handleAdd = () => {
        addToCart({
            productId: product.id,
            slug: product.slug,
            name: product.name,
            price: product.price,
            image: product.image,
            designKey: product.designKey,
            colorLabel: color?.label ?? null,
            colorHex: color?.hex ?? null,
            accentColor: product.accentColor,
            maxQuantity: product.madeToOrder ? null : product.stock
        })
    }

    return (
        <article className={cn('group relative flex flex-col', className)}>
            <div className='relative overflow-hidden rounded-[28px] shadow-[0_18px_44px_-30px_rgba(58,40,30,0.55)]'>
                <ProductImage
                    src={product.image}
                    alt={product.imageAlt ?? `${product.name}, a handmade crochet doll`}
                    designKey={product.designKey}
                    mainColor={color?.hex ?? null}
                    accentColor={product.accentColor}
                    priority={priority}
                    className='aspect-[4/5] w-full'
                    imageClassName='transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]'
                />

                <div className='pointer-events-none absolute left-3 top-3 flex flex-col items-start gap-1.5'>
                    {product.badge && (
                        <span className='rounded-full bg-cream-50/90 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-cocoa-800 shadow-sm backdrop-blur'>
                            <span data-mdlfy-cms={`products:${product.id}:badge`}>
                                {product.badge}
                            </span>
                        </span>
                    )}
                    {lowStock && (
                        <span className='rounded-full bg-blush-500/90 px-3 py-1 text-[11px] font-bold text-white shadow-sm'>
                            Only <span>{product.stock}</span> left
                        </span>
                    )}
                    {soldOut && (
                        <span className='rounded-full bg-cocoa-800/85 px-3 py-1 text-[11px] font-bold text-cream-50'>
                            Sold out
                        </span>
                    )}
                </div>

                <WishlistButton
                    item={{
                        productId: product.id,
                        slug: product.slug,
                        name: product.name,
                        price: product.price,
                        image: product.image,
                        designKey: product.designKey,
                        colorHex: color?.hex ?? null,
                        accentColor: product.accentColor
                    }}
                    className='absolute right-3 top-3 z-10'
                />

                {!soldOut && (
                    <button
                        type='button'
                        onClick={handleAdd}
                        aria-label={`Add ${product.name} to cart`}
                        className='absolute bottom-3 right-3 z-10 inline-flex h-11 items-center gap-2 rounded-full bg-cocoa-900/90 px-4 text-sm font-semibold text-cream-50 shadow-lg backdrop-blur transition-all duration-300 hover:bg-cocoa-800 lg:translate-y-3 lg:opacity-0 lg:group-hover:translate-y-0 lg:group-hover:opacity-100 lg:focus-visible:translate-y-0 lg:focus-visible:opacity-100'
                    >
                        <ShoppingBag className='size-4' />
                        <span className='hidden sm:inline'>Add</span>
                    </button>
                )}
            </div>

            <div className='mt-4 px-1'>
                {product.category && (
                    <p className={cn('text-[11px] font-bold uppercase tracking-[0.18em]', dark ? 'text-blush-200' : 'text-blush-600')}>
                        <span data-mdlfy-cms={`categories:${product.category.id}:name`}>
                            {product.category.name}
                        </span>
                    </p>
                )}
                <h3 className={cn('mt-1 font-heading text-xl leading-snug', dark ? 'text-cream-50' : 'text-cocoa-900')}>
                    <Link href={`/products/${product.slug}`} className='after:absolute after:inset-0 after:rounded-[28px] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-caramel-400'>
                        <span data-mdlfy-cms={`products:${product.id}:name`}>
                            {product.name}
                        </span>
                    </Link>
                </h3>
                <div className='mt-1.5 flex items-center justify-between gap-3'>
                    <p className={cn('text-[15px] font-bold', dark ? 'text-cream-100' : 'text-cocoa-700')}>
                        {formatIDR(product.price)}
                    </p>
                    {product.colors.length > 1 && (
                        <span className='flex items-center gap-1' aria-label={`${product.colors.length} colours`}>
                            {product.colors.slice(0, 4).map((swatch, index) => (
                                <span
                                    key={`${swatch.label}-${index}`}
                                    className='size-3.5 rounded-full border border-black/10'
                                    style={{ background: swatch.hex ?? '#f3c9cf' }}
                                />
                            ))}
                        </span>
                    )}
                </div>
            </div>
        </article>
    )
}
